import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { riskAssessmentKeys } from '@/features/dashboard/api/risk-assessments';
import { deductBalance, refundTransaction, SERVICES } from '@/features/payments';
import { apiClient } from '@/lib/api-client';
import { updatePaymentFlowStep, wizardKeys } from '../api/risk-assessment-wizard';

/**
 * Payment flow steps in sequence.
 */
export type PaymentFlowStep =
  | 'IDLE'
  | 'PAY_FOR_SEARCH'
  | 'SCREENING_PROGRESS'
  | 'DOWNLOAD_REPORT'
  | 'REQUEST_DOCS'
  | 'MATCH_REPORT'
  | 'EDD_UPSELL' // Shown after Match Report purchase to offer EDD upgrade
  | 'EDD' // Direct EDD purchase (from upsell or for resumption)
  | 'REPORT_GENERATING'
  | 'NO_ACCESS'
  | 'COMPLETE';

/**
 * State for tracking purchased services during the flow.
 */
export interface PurchasedServices {
  initialScreening: boolean;
  matchReport: boolean;
  eddReport: boolean;
  docRequest: boolean;
}

/**
 * Payment flow state.
 */
export interface PaymentFlowState {
  step: PaymentFlowStep;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  purchasedServices: PurchasedServices;
  isProcessing: boolean;
}

/**
 * API functions for the payment flow.
 */
async function runScreeningWithMode(
  assessmentId: string,
  enabledChecks: string[],
  adverseMediaMode: 'light' | 'dde',
) {
  return apiClient.fetch(`/risk-assessments/${assessmentId}/screen`, {
    method: 'POST',
    body: JSON.stringify({ enabledChecks, adverseMediaMode }),
  });
}

async function upgradeToEdd(assessmentId: string): Promise<{ success: boolean; jobId: string }> {
  return apiClient.fetch(`/risk-assessments/${assessmentId}/upgrade-to-edd`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

async function recordPaidService(
  assessmentId: string,
  serviceKey: string,
  transactionId: string,
): Promise<void> {
  await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
    method: 'POST',
    body: JSON.stringify({ serviceKey, transactionId }),
  });
}

async function generateDocumentLink(
  assessmentId: string,
  clientEmail: string,
  documentTypes: string[],
  otherDescription?: string,
) {
  return apiClient.fetch(`/risk-assessments/${assessmentId}/generate-document-link`, {
    method: 'POST',
    body: JSON.stringify({
      assessmentId,
      clientEmail,
      documentTypes,
      ...(otherDescription && { otherDescription }),
    }),
  });
}

async function sendReportReadyEmail(
  assessmentId: string,
): Promise<{ sent: boolean; message: string }> {
  return apiClient.fetch(`/risk-assessments/${assessmentId}/send-report-email`, {
    method: 'POST',
  });
}

async function completeWithoutReport(assessmentId: string): Promise<void> {
  await apiClient.fetch(`/risk-assessments/${assessmentId}/complete-without-report`, {
    method: 'POST',
  });
}

/** Steps that should be persisted to the database for resumption */
const PERSISTABLE_STEPS: PaymentFlowStep[] = ['REQUEST_DOCS', 'MATCH_REPORT', 'EDD_UPSELL', 'EDD'];

/** Map database step values to PaymentFlowStep */
function mapDbStepToFlowStep(dbStep: string | null): PaymentFlowStep | null {
  if (!dbStep) return null;
  const mapping: Record<string, PaymentFlowStep> = {
    REQUEST_DOCS: 'REQUEST_DOCS',
    MATCH_REPORT: 'MATCH_REPORT',
    EDD_UPSELL: 'EDD_UPSELL',
    EDD: 'EDD',
  };
  return mapping[dbStep] || null;
}

/**
 * Normalise a resume step based on feature flag state.
 * If document upload is disabled and the saved step is REQUEST_DOCS, skip to the next logical step.
 */
export function normalizeResumeStep(
  step: PaymentFlowStep | null,
  documentUploadEnabled: boolean,
  paidServices: { matchReport: boolean; eddReport: boolean },
): PaymentFlowStep | null {
  if (step === 'REQUEST_DOCS' && !documentUploadEnabled) {
    const hasPurchasedReport = paidServices.matchReport || paidServices.eddReport;
    return hasPurchasedReport ? 'REPORT_GENERATING' : 'COMPLETE';
  }
  return step;
}

/** Map PaymentFlowStep to database step values */
function mapFlowStepToDbStep(step: PaymentFlowStep): string | null {
  if (PERSISTABLE_STEPS.includes(step)) {
    return step;
  }
  return null;
}

/**
 * Hook to manage the payment flow for risk assessment screening.
 *
 * Flow:
 * 1. PAY_FOR_SEARCH - User pays £2.50 for initial screening
 * 2. SCREENING_PROGRESS - Show progress while checks run
 * 3. REQUEST_DOCS - Optional: Request documentation (£0.50) (when document_upload flag enabled)
 * 4. MATCH_REPORT - Optional: Purchase match report (£7.50)
 * 5. EDD - Optional: Purchase enhanced due diligence (£15.00)
 * 6. REPORT_GENERATING or NO_ACCESS - Based on purchases
 * 7. COMPLETE - Navigate to report
 *
 * @param assessmentId - The assessment ID
 * @param options.documentUploadEnabled - Whether document upload feature is enabled (default: true)
 */
export function usePaymentFlow(
  assessmentId: string,
  options: { documentUploadEnabled?: boolean } = {},
) {
  const { documentUploadEnabled = true } = options;
  const queryClient = useQueryClient();

  // Track if we've initialized from saved state to avoid re-initializing
  const hasInitialized = useRef(false);

  const [state, setState] = useState<PaymentFlowState>({
    step: 'IDLE',
    riskLevel: 'LOW',
    purchasedServices: {
      initialScreening: false,
      matchReport: false,
      eddReport: false,
      docRequest: false,
    },
    isProcessing: false,
  });

  // Persist step to database when it changes (only for persistable steps)
  const prevStepRef = useRef<PaymentFlowStep>(state.step);
  useEffect(() => {
    // Skip if step hasn't changed or if we just initialized
    if (prevStepRef.current === state.step) return;

    const wasIdle = prevStepRef.current === 'IDLE';
    prevStepRef.current = state.step;

    // Don't persist when initializing from saved state (IDLE -> saved step)
    if (wasIdle && hasInitialized.current) return;

    // Determine what to save to DB
    const dbStep = mapFlowStepToDbStep(state.step);

    // Only persist if:
    // 1. Step is a persistable step (REQUEST_DOCS, MATCH_REPORT, EDD), OR
    // 2. Step is COMPLETE or REPORT_GENERATING (clear the saved step)
    const shouldPersist =
      PERSISTABLE_STEPS.includes(state.step) ||
      state.step === 'COMPLETE' ||
      state.step === 'REPORT_GENERATING';

    if (shouldPersist) {
      // Fire and forget - don't block the UI
      updatePaymentFlowStep(assessmentId, dbStep).catch((err) => {
        console.warn('Failed to persist payment flow step:', err);
      });
    }
  }, [state.step, assessmentId]);

  // Mutation for running screening
  const screeningMutation = useMutation({
    mutationFn: ({
      enabledChecks,
      adverseMediaMode,
    }: {
      enabledChecks: string[];
      adverseMediaMode: 'light' | 'dde';
    }) => runScreeningWithMode(assessmentId, enabledChecks, adverseMediaMode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
      queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
    },
  });

  // Mutation for EDD upgrade
  const eddMutation = useMutation({
    mutationFn: () => upgradeToEdd(assessmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
      queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
    },
  });

  // Mutation for document link generation
  const docLinkMutation = useMutation({
    mutationFn: ({
      email,
      documentTypes,
      otherDescription,
    }: {
      email: string;
      documentTypes: string[];
      otherDescription?: string;
    }) => generateDocumentLink(assessmentId, email, documentTypes, otherDescription),
    onSuccess: () => {
      toast.success('Document request link sent to client');
    },
  });

  /**
   * Start the payment flow - shows the Pay for Search modal.
   */
  const startFlow = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'PAY_FOR_SEARCH',
      purchasedServices: {
        initialScreening: false,
        matchReport: false,
        eddReport: false,
        docRequest: false,
      },
    }));
  }, []);

  /**
   * Handle payment for initial screening.
   * If screening fails after payment, automatically refunds the transaction.
   */
  const handlePayForSearch = useCallback(
    async (enabledChecks: string[]) => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      let transactionId: string | null = null;

      try {
        // Deduct balance for screening
        const result = await deductBalance(SERVICES.PEP_SANCTIONS_SCREENING, assessmentId);
        transactionId = result.transactionId;

        // Record the purchase
        await recordPaidService(
          assessmentId,
          SERVICES.PEP_SANCTIONS_SCREENING,
          result.transactionId,
        );

        // Reset the screening status cache BEFORE showing the progress modal
        // This prevents stale COMPLETED status from showing all steps as green
        await queryClient.resetQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });

        // Move to screening progress (modal shows while screening starts)
        setState((prev) => ({
          ...prev,
          step: 'SCREENING_PROGRESS',
          isProcessing: false,
          purchasedServices: {
            ...prev.purchasedServices,
            initialScreening: true,
          },
        }));

        // Start screening - await to ensure cache is invalidated before polling checks status
        await screeningMutation.mutateAsync({
          enabledChecks,
          adverseMediaMode: 'light',
        });

        return true;
      } catch (error) {
        // If we have a transactionId, the payment succeeded but something else failed
        // Attempt to refund the user
        if (transactionId) {
          try {
            await refundTransaction(transactionId);
            // Invalidate balance queries to reflect the refund
            queryClient.invalidateQueries({ queryKey: ['balance'] });
            toast.error('Screening failed to start. Your payment has been refunded.');
          } catch (refundError) {
            // Refund failed - this is a critical error that needs attention
            console.error('Failed to refund transaction after screening failure:', refundError);
            toast.error(
              'Screening failed and we could not process your refund automatically. Please contact support.',
            );
          }
          // Reset state back to PAY_FOR_SEARCH so user can try again
          setState((prev) => ({
            ...prev,
            step: 'PAY_FOR_SEARCH',
            isProcessing: false,
            purchasedServices: {
              ...prev.purchasedServices,
              initialScreening: false,
            },
          }));
        } else {
          // Payment itself failed
          setState((prev) => ({ ...prev, isProcessing: false }));
          toast.error(error instanceof Error ? error.message : 'Payment failed');
        }
        return false;
      }
    },
    [assessmentId, screeningMutation, queryClient],
  );

  /**
   * Handle screening completion - move to DOWNLOAD_REPORT modal.
   * This allows users to download a basic PDF report before being offered the full report.
   */
  const handleScreeningComplete = useCallback(
    (riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL') => {
      setState((prev) => ({
        ...prev,
        step: 'DOWNLOAD_REPORT',
        riskLevel,
      }));
    },
    [],
  );

  /**
   * Proceed from download report modal to the Match Report modal.
   * ALL users (regardless of risk level) go to Match Report first.
   */
  const proceedFromDownloadReport = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'MATCH_REPORT',
    }));
  }, []);

  /**
   * Handle document request purchase.
   * After purchasing, check if a report was purchased - if yes, go to report generating;
   * if no, mark complete and go to COMPLETE (shows basic report viewer).
   */
  const handleDocRequest = useCallback(
    async (email: string, documentTypes: string[], otherDescription?: string) => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      try {
        // Deduct balance
        const result = await deductBalance(SERVICES.DOCUMENTATION_REQUEST, assessmentId);

        // Record the purchase
        await recordPaidService(assessmentId, SERVICES.DOCUMENTATION_REQUEST, result.transactionId);

        // Generate document link
        await docLinkMutation.mutateAsync({ email, documentTypes, otherDescription });

        const hasPurchasedReport =
          state.purchasedServices.matchReport || state.purchasedServices.eddReport;

        // If no report purchased, mark as complete
        if (!hasPurchasedReport) {
          try {
            await completeWithoutReport(assessmentId);
            queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
            queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
          } catch (error) {
            console.warn('Failed to mark assessment as complete:', error);
          }
        }

        // Go to REPORT_GENERATING if they purchased a report, otherwise COMPLETE
        // (shows basic report viewer since they paid for initial screening)
        setState((prev) => ({
          ...prev,
          step: hasPurchasedReport ? 'REPORT_GENERATING' : 'COMPLETE',
          isProcessing: false,
          purchasedServices: {
            ...prev.purchasedServices,
            docRequest: true,
          },
        }));
      } catch (error) {
        setState((prev) => ({ ...prev, isProcessing: false }));
        toast.error(error instanceof Error ? error.message : 'Document request failed');
      }
    },
    [
      assessmentId,
      docLinkMutation,
      state.purchasedServices.matchReport,
      state.purchasedServices.eddReport,
      queryClient,
    ],
  );

  /**
   * Skip document request - check if a report was purchased.
   * If yes, go to report generating; if no, mark as complete and go to COMPLETE
   * (shows basic report viewer since they paid for initial screening).
   */
  const skipDocRequest = useCallback(async () => {
    const hasPurchasedReport =
      state.purchasedServices.matchReport || state.purchasedServices.eddReport;

    if (!hasPurchasedReport) {
      // Mark assessment as complete without report
      try {
        await completeWithoutReport(assessmentId);
        // Invalidate queries to reflect the updated status
        queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
        queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      } catch (error) {
        console.warn('Failed to mark assessment as complete:', error);
      }
    }

    setState((prev) => ({
      ...prev,
      step: hasPurchasedReport ? 'REPORT_GENERATING' : 'COMPLETE',
    }));
  }, [
    assessmentId,
    state.purchasedServices.matchReport,
    state.purchasedServices.eddReport,
    queryClient,
  ]);

  /**
   * Handle match report purchase.
   * After purchasing, go to EDD_UPSELL to offer the EDD upgrade.
   */
  const handleMatchReportPurchase = useCallback(async () => {
    setState((prev) => ({ ...prev, isProcessing: true }));

    try {
      // Deduct balance
      const result = await deductBalance(SERVICES.MATCH_RISK_REPORT, assessmentId);

      // Record the purchase
      await recordPaidService(assessmentId, SERVICES.MATCH_RISK_REPORT, result.transactionId);

      // Invalidate screening status to reflect the purchase
      await queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });

      setState((prev) => ({
        ...prev,
        step: 'EDD_UPSELL',
        isProcessing: false,
        purchasedServices: {
          ...prev.purchasedServices,
          matchReport: true,
        },
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, isProcessing: false }));
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
    }
  }, [assessmentId, queryClient]);

  /**
   * Skip match report - go to REQUEST_DOCS (or skip it if document upload is disabled).
   */
  const skipMatchReport = useCallback(() => {
    if (!documentUploadEnabled) {
      // Skip REQUEST_DOCS entirely when document upload is disabled
      skipDocRequest();
      return;
    }
    setState((prev) => ({ ...prev, step: 'REQUEST_DOCS' }));
  }, [documentUploadEnabled, skipDocRequest]);

  /**
   * Skip EDD upsell - go directly to REPORT_GENERATING.
   * Sends report ready email since user has purchased the match report.
   */
  const skipEddUpsell = useCallback(async () => {
    // Send report ready email since user has purchased the match report
    try {
      await sendReportReadyEmail(assessmentId);
    } catch (emailError) {
      console.warn('Failed to send report ready email:', emailError);
    }

    setState((prev) => ({ ...prev, step: 'REPORT_GENERATING' }));
  }, [assessmentId]);

  /**
   * Handle EDD purchase.
   * Note: Email is NOT sent here - it should be sent when EDD processing completes
   * (handled by the backend or when status becomes COMPLETED).
   */
  const handleEddPurchase = useCallback(async () => {
    setState((prev) => ({ ...prev, isProcessing: true }));

    try {
      // Deduct balance
      const result = await deductBalance(SERVICES.AI_CONFIDENCE_REPORT, assessmentId);

      // Record the purchase
      await recordPaidService(assessmentId, SERVICES.AI_CONFIDENCE_REPORT, result.transactionId);

      // Trigger EDD upgrade (re-run adverse media in DDE mode)
      // This sets screeningJobStatus to PROCESSING on the server
      await eddMutation.mutateAsync();

      // Force refetch (not just invalidate) to ensure we have the PROCESSING status
      // before setting the step. This prevents the race condition where stale COMPLETED
      // status triggers immediate navigation to report.
      await queryClient.refetchQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });

      setState((prev) => ({
        ...prev,
        step: 'REPORT_GENERATING',
        isProcessing: false,
        purchasedServices: {
          ...prev.purchasedServices,
          eddReport: true,
        },
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, isProcessing: false }));
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
    }
  }, [assessmentId, eddMutation, queryClient]);

  /**
   * Skip EDD - go to REQUEST_DOCS (or skip it if document upload is disabled).
   */
  const skipEdd = useCallback(() => {
    if (!documentUploadEnabled) {
      skipDocRequest();
      return;
    }
    setState((prev) => ({ ...prev, step: 'REQUEST_DOCS' }));
  }, [documentUploadEnabled, skipDocRequest]);

  /**
   * Go back to purchase a report (from NO_ACCESS).
   * ALL users go to Match Report regardless of risk level.
   */
  const goBackToPurchase = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'MATCH_REPORT',
    }));
  }, []);

  /**
   * Mark flow as complete.
   */
  const completeFlow = useCallback(() => {
    setState((prev) => ({ ...prev, step: 'COMPLETE' }));
  }, []);

  /**
   * Reset the flow.
   */
  const resetFlow = useCallback(() => {
    setState({
      step: 'IDLE',
      riskLevel: 'LOW',
      purchasedServices: {
        initialScreening: false,
        matchReport: false,
        eddReport: false,
        docRequest: false,
      },
      isProcessing: false,
    });
  }, []);

  /**
   * Set step directly (for external control).
   */
  const setStep = useCallback((step: PaymentFlowStep) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  /**
   * Initialize the flow from screening status (for resumption after page reload).
   * Should be called when screening status data loads.
   */
  const initializeFromStatus = useCallback(
    (status: {
      paymentFlowStep: string | null;
      riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
      paidServices: {
        initialScreening: boolean;
        matchReport: boolean;
        eddReport: boolean;
        docRequest: boolean;
      };
    }) => {
      // Only initialize once and only if we're in IDLE state
      if (hasInitialized.current) return;

      const resumeStep = normalizeResumeStep(
        mapDbStepToFlowStep(status.paymentFlowStep),
        documentUploadEnabled,
        status.paidServices,
      );

      if (resumeStep) {
        hasInitialized.current = true;
        setState((prev) => ({
          ...prev,
          step: resumeStep,
          riskLevel: status.riskLevel,
          purchasedServices: status.paidServices,
        }));
      }
    },
    [documentUploadEnabled],
  );

  return {
    state,
    startFlow,
    handlePayForSearch,
    handleScreeningComplete,
    proceedFromDownloadReport,
    handleDocRequest,
    skipDocRequest,
    handleMatchReportPurchase,
    skipMatchReport,
    skipEddUpsell,
    handleEddPurchase,
    skipEdd,
    goBackToPurchase,
    completeFlow,
    resetFlow,
    setStep,
    initializeFromStatus,
    isScreeningPending: screeningMutation.isPending,
    isEddPending: eddMutation.isPending,
  };
}
